/**
 * Presentation rules for reported numbers.
 *
 * The backend sends `null` for any rate it could not compute, and that is a
 * different thing from `0`: "no offers made yet" must never render as
 * "0% accepted". Every helper here keeps the two apart.
 */

/** A rate the API already rounded, or an em dash when there was nothing to divide. */
export function percent(value) {
  return value == null ? '—' : `${value}%`;
}

/** A duration in days, singular where it matters. */
export function days(value) {
  if (value == null) return '—';
  if (value < 1) return 'same day';
  return value === 1 ? '1 day' : `${value} days`;
}

export function count(value) {
  return new Intl.NumberFormat().format(value ?? 0);
}

export function rating(value) {
  return value == null ? '—' : `${value} / 5`;
}

const BUCKET_FORMATS = {
  DAY: { day: 'numeric', month: 'short' },
  WEEK: { day: 'numeric', month: 'short' },
  MONTH: { month: 'short', year: '2-digit' },
};

/** Axis label for one bucket, at the resolution the server chose. */
export function bucketLabel(value, granularity = 'DAY') {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, BUCKET_FORMATS[granularity] ?? BUCKET_FORMATS.DAY);
}

/** Wording for the tooltip on a point, which needs the bucket's width spelled out. */
export function bucketTitle(value, granularity = 'DAY') {
  const label = bucketLabel(value, granularity);
  if (granularity === 'WEEK') return `Week of ${label}`;
  if (granularity === 'MONTH') return label;
  return label;
}

/** The periods a reader can choose. Anything longer is plotted per month. */
export const PERIODS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '12 months' },
];
